import React from "react";
import {
  BaselineDefaultsScalingForm,
  BaselineDefaultsScalingFormDefaults,
  BaselineDefaultsScalingFormDivision,
  DefaultsPageConfiguration
} from "api";
import utils from "utils";
import listUtils from "utils/listUtils";
import Dropdown from "components/controls/Dropdown";
import Label from "components/controls/Label";
import PrimaryButton from "components/controls/PrimaryButton";
import Section from "components/controls/Section";
import Spacer from "components/controls/Spacer";
import Column from "components/controls/Column";
import ContentArea from "components/layouts/ContentArea";
import Heading from "components/controls/Heading";
import DefaultsScalingFormTable from "../DefaultsScalingFormTable";

interface DefautlsScalingFormProps {
  setForm: (form: BaselineDefaultsScalingForm) => any;
  form: BaselineDefaultsScalingForm;
  submit: () => void;
  isFetching: boolean;
  onAddRowAction?: (() => void) | undefined;
  hideDivisions?: boolean | undefined;
  hideSaveButton?: boolean | undefined;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

const DefautlsScalingForm: React.FC<DefautlsScalingFormProps> = (props) => {
  const cloneForm = function (): BaselineDefaultsScalingForm {
    return utils.cloneObject(props.form);
  };

  const setForm = function (form: BaselineDefaultsScalingForm) {
    props.setForm(form);
  };

  const onDivisionDefaultsChange = (
    defaults: BaselineDefaultsScalingFormDefaults[]
  ) => {
    onDivisionChanged({
      ...props.form.selectedDivision,
      defaults: (defaults && defaults.length > 0 && defaults[0]) || undefined,
      isChanged: true
    });
  };

  const onBedroomsDefaultsChange = (
    defaults: BaselineDefaultsScalingFormDefaults[]
  ) => {
    onDivisionChanged({
      ...props.form.selectedDivision,
      bedroomDefaults: defaults,
      isChanged: true
    });
  };

  const onPropertiesDefaultsChange = (
    defaults: BaselineDefaultsScalingFormDefaults[]
  ) => {
    onDivisionChanged({
      ...props.form.selectedDivision,
      propertyBedroomDefaults: defaults,
      isChanged: true
    });
  };

  const addBedroomDefaultsRow = function (): void {
    const selectedDivision = props.form && props.form.selectedDivision;
    const newRow = {
      ...(selectedDivision && selectedDivision.defaults),
      formValidationErrors: undefined,
      fieldValidationErrors: undefined,
      isValid: false,
      isChanged: true
    };
    const currentRows =
      (selectedDivision && selectedDivision.bedroomDefaults) || [];

    onBedroomsDefaultsChange(currentRows.concat(newRow));
  };

  const addPropertyDefaultsRow = function (): void {
    const selectedDivision = props.form && props.form.selectedDivision;
    const newRow = {
      ...(selectedDivision && selectedDivision.defaults),
      formValidationErrors: undefined,
      fieldValidationErrors: undefined,
      isValid: false,
      isChanged: true
    };
    const currentRows =
      (selectedDivision && selectedDivision.propertyBedroomDefaults) || [];

    onPropertiesDefaultsChange(currentRows.concat(newRow));
  };

  const onDivisionChanged = (
    division: BaselineDefaultsScalingFormDivision | undefined
  ) => {
    const divisions = (props.form && props.form.divisions) || undefined;
    let newDivisionsList = divisions;

    if (divisions && division) {
      newDivisionsList =
        listUtils.copyListWithNewKeyedItem(divisions, division) || undefined;
    }

    const form = cloneForm();

    form.selectedDivision = division;
    form.divisions = newDivisionsList;

    setForm(form);
  };

  let selectedDivision = undefined;
  let fieldValidationErrors:
    | {
        [key: string]: string;
      }
    | undefined = undefined;
  const form = props && props.form;
  if (form) {
    selectedDivision = form.selectedDivision;
    fieldValidationErrors = form.fieldValidationErrors;
  }

  const getDivisionDropdownOrLabel = function (): JSX.Element {
    if (props.hideDivisions) {
      return <div />;
    }

    const divisions = props.form.divisions;
    const selectedDivision = props.form.selectedDivision;
    if (divisions && divisions.length > 1) {
      return (
        <Dropdown
          onChange={onDivisionChanged}
          hideClear={true}
          options={divisions}
          selectedValue={selectedDivision}
          validationMessage={
            fieldValidationErrors && fieldValidationErrors["SelectedDivision"]
          }
        />
      );
    } else if (selectedDivision) {
      return <Label label={selectedDivision.displayValue} />;
    } else {
      return <div />;
    }
  };

  const getSaveButton = function (): JSX.Element {
    if (props.hideSaveButton) {
      return <div />;
    }

    return (
      <PrimaryButton
        disabled={props.isFetching}
        onClick={() => props.submit()}
        title="SAVE"
      />
    );
  };

  const divisionsArea = getDivisionDropdownOrLabel();
  const saveButton = getSaveButton();

  return (
    <div>
      <Section title="SCALING" />

      <Column size="2">{divisionsArea}</Column>

      <Spacer size="l" orientation="v" />

      <ContentArea columnSize={"12"}>
        <Heading level="3">Division Defaults (% increases)</Heading>
        <DefaultsScalingFormTable
          defaults={
            (props &&
              props.form &&
              props.form.selectedDivision &&
              props.form.selectedDivision.defaults && [
                props.form.selectedDivision.defaults
              ]) ||
            undefined
          }
          onChange={onDivisionDefaultsChange}
          hidePropertyColumn={true}
          hideBedroomTypeColumn={true}
          hideRemoveRowColumn={
            !(
              props.form.pageConfiguration &&
              props.form.pageConfiguration.isEditEnabled
            )
          }
          paging={false}
          bedrooms={[]}
          properties={[]}
          includeRemoveRowColumn="buffer"
          pageConfiguration={props.pageConfiguration}
        />
      </ContentArea>

      <Spacer size="l" orientation="v" />
      <ContentArea columnSize={"12"}>
        <Heading level="3">By Bedroom (% increases)</Heading>
        <DefaultsScalingFormTable
          defaults={
            (props &&
              props.form &&
              props.form.selectedDivision &&
              props.form.selectedDivision.bedroomDefaults) ||
            undefined
          }
          onChange={onBedroomsDefaultsChange}
          hidePropertyColumn={true}
          hideRemoveRowColumn={
            !(
              props.form.pageConfiguration &&
              props.form.pageConfiguration.isEditEnabled
            )
          }
          bedrooms={
            (selectedDivision &&
              selectedDivision.bedroomTypesForBedroomDefaults) ||
            []
          }
          properties={
            (selectedDivision && selectedDivision.propertyTypes) || []
          }
          onAddRowAction={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? addBedroomDefaultsRow
              : props.onAddRowAction
          }
          includeRemoveRowColumn={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? "show"
              : undefined
          }
          pageConfiguration={props.pageConfiguration}
        />
      </ContentArea>

      <Spacer size="l" orientation="v" />

      <ContentArea columnSize={"12"}>
        <Heading level="3">By Property (% increases)</Heading>
        <DefaultsScalingFormTable
          defaults={
            (props &&
              props.form &&
              props.form.selectedDivision &&
              props.form.selectedDivision.propertyBedroomDefaults) ||
            undefined
          }
          onChange={onPropertiesDefaultsChange}
          hideRemoveRowColumn={
            !(
              props.form.pageConfiguration &&
              props.form.pageConfiguration.isEditEnabled
            )
          }
          bedrooms={
            (selectedDivision &&
              selectedDivision.bedroomTypesForPropertyBedroomDefaults) ||
            []
          }
          properties={
            (selectedDivision && selectedDivision.propertyTypes) || []
          }
          onAddRowAction={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? addPropertyDefaultsRow
              : props.onAddRowAction
          }
          includeRemoveRowColumn={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? "show"
              : undefined
          }
          pageConfiguration={props.pageConfiguration}
        />
      </ContentArea>

      <Spacer size="l" orientation="v" />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {saveButton}
      </div>
    </div>
  );
};

export default DefautlsScalingForm;
